/**
 * Translation between the handbook wire shapes and the app's own.
 *
 * Two words change here and nowhere else: the server's `ACTIVE` is the app's
 * `published`, and a stored file's MIME type becomes the `kind` the two upload
 * buttons and the row icon read.
 */

import { HandbookAttachment, HandbookSection, HandbookStatus } from '@core/models';
import { attachmentKind } from '@core/util/attachment.util';

import { HandbookSectionDto, PublicationStatusDto, StoredFileDto } from './handbook.dto';

/** The app's word for each of the server's four. */
const STATUS: Record<PublicationStatusDto, HandbookStatus> = {
  DRAFT: 'draft',
  IN_REVIEW: 'in-review',
  ACTIVE: 'published',
  ARCHIVED: 'archived',
};

/** And back, for a write. */
const WIRE_STATUS: Record<HandbookStatus, PublicationStatusDto> = {
  draft: 'DRAFT',
  'in-review': 'IN_REVIEW',
  published: 'ACTIVE',
  archived: 'ARCHIVED',
};

/**
 * One section. Attachments are a separate read, so a section arrives with none
 * and they are merged in when its panel is opened.
 */
export function handbookSectionFromDto(dto: HandbookSectionDto): HandbookSection {
  return {
    id: dto.sectionGuid,
    title: dto.title,
    status: STATUS[dto.publicationStatus] ?? 'draft',
    // The server's own flag, not `status === 'published'`: it derives the pair
    // together and told us to read this one rather than recompute it.
    published: dto.published,
    author: dto.authorName ?? '',
    updated: dto.updatedDt,
    body: dto.body ?? '',
    attachments: [],
  };
}

/** The status word a write sends. */
export function handbookStatusToDto(status: HandbookStatus): PublicationStatusDto {
  return WIRE_STATUS[status] ?? 'DRAFT';
}

/** One attached file, as a row renders it. */
export function handbookAttachmentFromDto(dto: StoredFileDto): HandbookAttachment {
  return {
    id: dto.guid,
    name: dto.filename,
    size: dto.sizeBytes,
    type: dto.contentType,
    kind: attachmentKind(dto.contentType),
    uploadedOn: dto.uploadedTs,
    url: dto.downloadUrl ?? null,
  };
}
